import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import ImageIcon from "./icon/ImageIcon";
import styled from "@emotion/styled";
import useFileCreate from "src/hooks/files/useFileCreate";
import { enqueueSnackbar } from "notistack";
import appConfig from "src/config/env";

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function UpFileBtn({ onChange, error, value, disabled, specific, ...props }) {
  const [reviewImg, setReviewImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { trigger: createFile } = useFileCreate();

  useEffect(() => {
    if (value?.src) {
      setReviewImg(`${appConfig.API_FILE}/${value.src}`);
    }
  }, [value?.src]);

  const handleGetImg = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Vui lòng nhập hình ảnh đúng format", {
        variant: "warning",
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setIsError(true);
      enqueueSnackbar("Hình ảnh vượt quá 3 MB", {
        variant: "warning",
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setReviewImg(previewUrl);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("files", file);

      const res = await createFile({
        body: formData,
      });

      if (res?.success) {
        setIsError(false);
        setReviewImg(`${appConfig.API_FILE}/${res.data[0].src}`);
        onChange?.(res.data[0]._id);

        enqueueSnackbar("Thành công", {
          variant: "success",
        });
      } else {
        URL.revokeObjectURL(previewUrl);
        enqueueSnackbar(res?.error?.message || "Tải hình ảnh thất bại", {
          variant: "error",
        });
      }
    } catch (error) {
      URL.revokeObjectURL(previewUrl);
      enqueueSnackbar(error?.message || "Có lỗi xảy ra trong quá trình tải ảnh", {
        variant: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Box
        {...props}
        sx={{
          height: "100%",
          minWidth: "120px",
          ...props?.sx,
        }}
      >
        <Button
          disabled={disabled || uploading}
          fullWidth
          component="label"
          sx={{
            border: error ? "2px dashed red" : "2px dashed var(--black)",
            height: "100%",
            background: reviewImg ? `url(${reviewImg}) center / cover no-repeat` : "none",
            p: 0,
            position: "relative",
          }}
        >
          {!!reviewImg && !uploading && (
            <Typography
              variant="body2"
              sx={{
                background: "#28282d8f",
                position: "absolute",
                bottom: 0,
                color: "var(--white)",
                width: "100%",
                textAlign: "center",
                py: 0.5,
              }}
            >
              Đổi hình ảnh
            </Typography>
          )}

          {!reviewImg && !uploading && <ImageIcon />}

          {uploading && <CircularProgress aria-label="Loading…" />}

          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (file) {
                handleGetImg(file);
              }

              // Allow selecting the same file again
              event.target.value = "";
            }}
          />
        </Button>
      </Box>
      {specific && (
        <Typography
          color={isError ? "error" : "default"}
          variant="caption"
        >
          Hình ảnh dưới 3MB
        </Typography>
      )}
    </>
  );
}
